import type { AgentState, BotRow } from './types';
import NexusFace, { MarketingFace, SalesFace, WebFace } from '../NexusFace';

/** The general manager wears the Nexus face; the website bot wears its own. */
export function avatarGlyph(bot: Pick<BotRow, 'glyph' | 'is_master' | 'kind'>): string {
  if (bot.is_master) return 'nexus';
  if (bot.kind === 'website') return 'web';
  if (bot.kind === 'marketing') return 'marketing';
  if (bot.kind === 'sales') return 'sales';
  return bot.glyph;
}

const SHAPES: Record<string, string> = {
  triangle: 'M24 4 44 42H4L24 4Z',
  circle: 'M24 4a20 20 0 1 1 0 40 20 20 0 0 1 0-40Z',
  square: 'M6 6h36v36H6Z',
  diamond: 'M24 3 45 24 24 45 3 24 24 3Z',
  hexagon: 'M24 3 42 13.5v21L24 45 6 34.5v-21L24 3Z',
};

const EYES: Record<string, [number, number, number, number]> = {
  triangle: [18, 30, 30, 30],
  circle: [17, 24, 31, 24],
  square: [17, 24, 31, 24],
  diamond: [18, 24, 30, 24],
  hexagon: [17, 24, 31, 24],
};

export function stateDot(state: AgentState | undefined): string {
  switch (state) {
    case 'thinking':
    case 'working': return 'bg-emerald-400 animate-pulse';
    case 'waiting':
    case 'blocked': return 'bg-rose-500';
    case 'done': return 'bg-sky-400';
    case 'failed': return 'bg-rose-500';
    default: return 'bg-zinc-500';
  }
}

/** Drop internal setup labels. "Computer connected" is the machine coming online, not the bot using it. */
export function ownerActivity(activity: string | null | undefined): string | null {
  const text = (activity || '').trim();
  if (!text) return null;
  if (/^(computer connected|environment connected|starting the computer|session started)$/i.test(text)) return null;
  return text;
}

export function stateLabel(state: AgentState | undefined): string {
  switch (ownerFacing(state).kind) {
    case 'working': return 'Working';
    case 'needs': return 'Waiting for you';
    case 'finished': return state === 'failed' ? 'Stopped' : 'Finished';
    default: return 'Idle';
  }
}

/** Readable model id: gpt-6.1-sol → GPT-6.1 Sol, gpt-image-2.5-sunburst → GPT Image 2.5 Sunburst. */
export function modelLabel(id: string | null | undefined): string {
  const raw = (id || '').trim();
  if (!raw) return '';
  const parts = raw.split('-').filter(Boolean);
  const out: string[] = [];
  parts.forEach((part, i) => {
    if (i === 0 && part.toLowerCase() === 'gpt') { out.push('GPT'); return; }
    const prev = out[out.length - 1];
    // Keep the version glued to GPT: GPT-6.1, GPT-4o.
    if (prev === 'GPT' && /^\d/.test(part)) { out[out.length - 1] = `GPT-${part}`; return; }
    out.push(/^\d/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1));
  });
  return out.join(' ');
}

/** What the owner should see. Internal pauses while a bot is still going count as working. */
export function ownerFacing(state: AgentState | undefined): { kind: 'working' | 'needs' | 'finished' | 'idle'; label: string } {
  if (state === 'thinking' || state === 'working') return { kind: 'working', label: 'Working' };
  if (state === 'blocked' || state === 'waiting') return { kind: 'needs', label: 'Waiting for you' };
  if (state === 'done') return { kind: 'finished', label: 'Finished' };
  if (state === 'failed') return { kind: 'finished', label: 'Stopped' };
  return { kind: 'idle', label: 'Idle' };
}

const SEEN_PREFIX = 'agents:status-seen:';

export function readStatusSeen(businessId: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(`${SEEN_PREFIX}${businessId}`);
    const parsed = raw ? JSON.parse(raw) as unknown : {};
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
}

export function writeStatusSeen(businessId: string, seen: Record<string, string>) {
  try { localStorage.setItem(`${SEEN_PREFIX}${businessId}`, JSON.stringify(seen)); } catch { /* private mode */ }
  window.dispatchEvent(new CustomEvent('agents:status-seen'));
}

/** Working stays visible. Finished and waiting clear once this chat has been opened. */
export function unseenStatus(status: AgentState | undefined, updatedAt: string | null | undefined, seenAt: string | undefined): AgentState | undefined {
  const kind = ownerFacing(status).kind;
  if (kind === 'working') return status;
  if (kind !== 'needs' && kind !== 'finished') return undefined;
  const updated = updatedAt ? Date.parse(updatedAt) : NaN;
  const seen = seenAt ? Date.parse(seenAt) : NaN;
  if (Number.isFinite(seen) && Number.isFinite(updated) && updated <= seen) return undefined;
  return status;
}

export default function BotAvatar({
  glyph,
  colour,
  state,
  size = 36,
  showDot = false,
}: {
  glyph: string;
  colour: string;
  state?: AgentState;
  size?: number;
  showDot?: boolean;
}) {
  const path = SHAPES[glyph] || SHAPES.triangle;
  const eyes = EYES[glyph] || EYES.triangle;
  const pulse = state === 'thinking' || state === 'working';
  return (
    <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      {glyph === 'nexus' ? (
        <NexusFace size={size} pulse={pulse} />
      ) : glyph === 'web' ? (
        <WebFace size={size} pulse={pulse} />
      ) : glyph === 'marketing' ? (
        <MarketingFace size={size} pulse={pulse} />
      ) : glyph === 'sales' ? (
        <SalesFace size={size} pulse={pulse} />
      ) : (
        <svg viewBox="0 0 48 48" className={`h-full w-full ${pulse ? 'animate-pulse' : ''}`} aria-hidden="true">
          <path d={path} fill={colour} />
          <circle cx={eyes[0]} cy={eyes[1]} r="2.2" fill="#1c1917" />
          <circle cx={eyes[2]} cy={eyes[3]} r="2.2" fill="#1c1917" />
        </svg>
      )}
      {showDot && (
        <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-zinc-950 ${stateDot(state)}`} />
      )}
    </span>
  );
}
